import * as React from 'react';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Stack from '@mui/material/Stack';
import { Box } from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { archiveGroupMessage, copyGroupMessage, undoArchiveGroupMessage } from 'src/shared/services/groupMessageService';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { toast } from 'react-toastify';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { useState } from 'react';

type HandleGroupMessageMenuProps = {
  groupMessageId: string;
  disabled: boolean;
  callback: () => void;
  deletedAt: string | null;
  messageIsSent: boolean;
  refetchCallback: (refetch: boolean) => void;
};

export const HandleGroupMessageMenu = (props: HandleGroupMessageMenuProps) => {
  const { disabled, groupMessageId, callback, deletedAt, messageIsSent, refetchCallback } = props;
  const { t } = useTranslation(['groupMessage', 'common']);
  const isMounted = useIsMounted();

  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  const handleCopy = async () => {
    try {
      await copyGroupMessage(groupMessageId);
      if (isMounted()) {
        toast.success(<>{t('groupMessage:copyGroupMessage.success')}</>);
        setOpen(false);
        callback();
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    }
  };

  const handleArchive = async () => {
    try {
      if (!deletedAt) {
        await archiveGroupMessage(groupMessageId);
      } else {
        await undoArchiveGroupMessage(groupMessageId);
      }
      if (isMounted()) {
        toast.success(<>{deletedAt ? t('groupMessage:archiveGroupMessage.undoArchiveSuccess') : t('groupMessage:archiveGroupMessage.success')}</>);
        setOpen(false);
        // navigate(`${RoutesEnum.GroupMessageEmailOverview}`);
        refetchCallback(true);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.error(<>{deletedAt ? t('groupMessage:archiveGroupMessage.undoArchiveError') : t('groupMessage:archiveGroupMessage.error')}</>);
      }
    } finally {
      setOpenDeleteDialog(false);
    }
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current?.focus();
    }
    prevOpen.current = open;
  }, [open]);

  return (
    <>
      <Stack direction='row' spacing={2} sx={{ ml: 2 }}>
        <Box>
          <Button
            ref={anchorRef}
            id='composition-button'
            aria-controls={open ? 'composition-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup='true'
            onClick={handleToggle}
            variant='contained'
            disabled={disabled}
            disableElevation
            endIcon={<ArrowDropDown />}
          >
            {t('common:button.handle')}
          </Button>
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            role={undefined}
            placement='bottom-start'
            transition
            disablePortal
            nonce={undefined}
            sx={{ zIndex: 999 }}
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleClose}>
                    <>
                      <MenuList id='composition-menu' aria-labelledby='composition-button'>
                        <MenuItem onClick={handleCopy}>{t('common:button.copy')}</MenuItem>
                      </MenuList>
                      {!messageIsSent && (
                        <MenuList id='composition-menu' aria-labelledby='composition-button'>
                          <MenuItem onClick={() => setOpenDeleteDialog(true)}>
                            {deletedAt ? t('groupMessage:archiveGroupMessage.undoArchive') : t('groupMessage:archiveGroupMessage.archive')}
                          </MenuItem>
                        </MenuList>
                      )}
                    </>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Box>
      </Stack>
      <DeleteConfirmationDialog
        title={
          deletedAt
            ? t('groupMessage:archiveGroupMessage.undoArchiveConfirmationTitle')
            : t('groupMessage:archiveGroupMessage.archiveConfirmationTitle')
        }
        value={groupMessageId}
        modaltext={
          deletedAt
            ? t('groupMessage:archiveGroupMessage.undoArchiveConfirmationText')
            : t('groupMessage:archiveGroupMessage.archiveConfirmationText')
        }
        onClose={(value) => {
          if (value) {
            handleArchive();
          } else {
            setOpenDeleteDialog(false);
          }
        }}
        open={openDeleteDialog}
      />
    </>
  );
};
